/**
 * Agency CRM workspace (docs/specs/partner-crm.md): pipeline, leads, contacts, companies, tasks, activities and CSV import for a
 * partner agency's OWN business, over /v1/crm. Surface-neutral: the caller passes the translator and transport, so
 * the partner routes wrap it thinly. Record text is untrusted: rendered in <bdi> so bidi marks cannot reorder the page.
 *
 * Layout follows Google Workspace / Material 3: primary action in the page header (a floating action button on phones),
 * one-row section tabs, a toolbar (search, filter chips, count), flat list rows with hairline dividers, and every create
 * form in a dialog (a bottom sheet on phones) instead of an always-open box.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router';
import { DEAL_STAGES } from '@qm/contracts/crm';
import { Dialog, KitProvider } from '@qm/ui/kit';
import { agencyCrmApi, mappingFromCsv, type AgencyCrmApi, type Availability, type CrmEntityPath, type CrmHttp, type CrmRow, type ImportPreview } from './api';
import './agency-crm.css';

export type Tr = (key: string, params?: Record<string, string | number>) => string;
export const CRM_SECTIONS = ['pipeline', 'leads', 'contacts', 'companies', 'tasks', 'activities', 'import'] as const;
export type CrmSection = (typeof CRM_SECTIONS)[number];
export const sectionPath = (base: string, s: CrmSection) => (s === 'pipeline' ? base : `${base}/${s}`);

export interface AgencyCrmProps {
  http: CrmHttp;
  t: Tr;
  locale: string;
  section: CrmSection;
  /** route base, e.g. "/crm" */
  base: string;
  /** server-rendered / test data keyed by section; skips the first fetch */
  preload?: Partial<Record<CrmSection | 'availability', unknown>>;
  /**
   * Where the primary action ("+ New deal") goes: the page header's action area.
   * undefined = render it inline at the top; null = the slot is not mounted yet (render nothing).
   * The phone floating action button is rendered by the workspace either way.
   */
  actionSlot?: HTMLElement | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const Text = ({ v }: { v: unknown }) => <bdi>{str(v) || '-'}</bdi>;

function money(locale: string, minor: unknown, currency: unknown): string {
  const cur = /^[A-Z]{3}$/.test(str(currency)) ? str(currency) : 'AED';
  try { return new Intl.NumberFormat(locale, { style: 'currency', currency: cur }).format(Number(minor ?? 0) / 100); } catch { return `${cur} ${Number(minor ?? 0) / 100}`; }
}

/** Error key for a failed call: 403 means the role may read but not write. */
function errorKey(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  return status === 403 ? 'crm.agency.forbidden' : 'crm.agency.error';
}

/** Case-insensitive match of the search text against every text value of a row. */
export function matchesQuery(row: CrmRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return Object.entries(row).some(([k, v]) => k !== 'id' && (typeof v === 'string' || typeof v === 'number') && String(v).toLowerCase().includes(q));
}

/** Rows grouped into the pipeline stages (search applied), with each stage's total. */
export function dealColumns(rows: CrmRow[], query = '') {
  const shown = rows.filter((d) => matchesQuery(d, query));
  return DEAL_STAGES.map((stage) => {
    const items = shown.filter((d) => d.stage === stage);
    return { stage, items, total: items.reduce((sum, d) => sum + Number(d.valueMinor ?? 0), 0) };
  });
}

export const dealPayload = (v: Record<string, string>, currency: string) => ({ title: v.title, valueMinor: Math.round(Number(v.value || 0) * 100), currency, stage: 'lead' });

const countKey = (entity: string, n: number) => `crm.agency.count.${entity}.${n === 1 ? 'one' : 'other'}`;

function useRows(load: () => Promise<CrmRow[]>, initial: unknown) {
  const [state, setState] = useState<{ rows: CrmRow[] | null; error: unknown }>({ rows: Array.isArray(initial) ? (initial as CrmRow[]) : null, error: null });
  const [tick, setTick] = useState(0);
  const skip = useRef(Array.isArray(initial));
  const loader = useRef(load);
  loader.current = load;
  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    let live = true;
    loader.current().then((rows) => { if (live) setState({ rows, error: null }); }, (error: unknown) => { if (live) setState({ rows: null, error }); });
    return () => { live = false; };
  }, [tick]);
  return { ...state, reload: useCallback(() => setTick((n) => n + 1), []) };
}

function useMutation(t: Tr, after: () => void) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setPending(true); setError(null);
    try { await fn(); after(); return true; } catch (e) { setError(t(errorKey(e))); return false; } finally { setPending(false); }
  };
  return { pending, error, run, reset: useCallback(() => setError(null), []) };
}
type Mutation = ReturnType<typeof useMutation>;

const Plus = () => (
  <svg className="ac-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);
const Magnifier = () => (
  <svg className="ac-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" /><path d="m16 16 4.5 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);

const initialOf = (v: unknown) => Array.from(str(v).trim())[0]?.toUpperCase() || '?';
const Avatar = ({ v }: { v: unknown }) => <span className="ac-avatar" aria-hidden="true"><bdi>{initialOf(v)}</bdi></span>;

/* ---------- page furniture: toolbar, chips, empty state, error note ---------- */

function Toolbar({ t, query, onQuery, count, children }: { t: Tr; query: string; onQuery: (q: string) => void; count: string; children?: ReactNode }) {
  return (
    <div className="ac-bar" role="search">
      <label className="ac-search">
        <Magnifier />
        <input type="search" value={query} onChange={(e) => onQuery(e.target.value)} placeholder={t('crm.agency.search')} aria-label={t('crm.agency.search')} autoComplete="off" />
      </label>
      {children}
      <p className="ac-count" role="status" data-testid="crm-count">{count}</p>
    </div>
  );
}

interface FilterDef { id: string; label: string; test: (r: CrmRow) => boolean }
function Chips({ label, filters, value, onChange }: { label: string; filters: FilterDef[]; value: string; onChange: (id: string) => void }) {
  return (
    <div className="ac-chips" role="group" aria-label={label}>
      {filters.map((f) => <button key={f.id} type="button" className="ac-chip" aria-pressed={value === f.id} onClick={() => onChange(f.id)}>{f.label}</button>)}
    </div>
  );
}

function ErrorNote({ message }: { message: string | null }) {
  return message ? <p role="alert" className="ac-note" data-tone="bad">{message}</p> : null;
}

function Empty({ t, filtered, onCreate, createLabel }: { t: Tr; filtered: boolean; onCreate?: () => void; createLabel?: string }) {
  return (
    <div className="ac-empty" data-testid="crm-empty">
      <p>{filtered ? t('crm.agency.noMatch') : t('crm.agency.empty')}</p>
      {!filtered && onCreate ? <button type="button" className="ac-btn" onClick={onCreate}>{createLabel}</button> : null}
    </div>
  );
}

function Status({ rows, error, t }: { rows: CrmRow[] | null; error: unknown; t: Tr }) {
  if (error) return <p role="alert" className="ac-note" data-tone="bad">{t('crm.agency.error')}</p>;
  if (!rows) return <p className="ac-note" aria-busy="true">{t('crm.agency.loading')}</p>;
  return null;
}

/* ---------- create dialog (desktop) / bottom sheet (phone) ---------- */

interface FieldDef { name: string; label: string; type?: 'text' | 'email' | 'number' | 'select'; required?: boolean; options?: { value: string; label: string }[]; initial?: string }

function FormBody({ id, fields, error, pending, onSubmit, onReset }: { id: string; fields: FieldDef[]; error: string | null; pending: boolean; onSubmit: (v: Record<string, string>) => void; onReset: () => void }) {
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(fields.filter((f) => f.initial).map((f) => [f.name, f.initial!])));
  useEffect(onReset, [onReset]);
  const submit = (e: FormEvent) => { e.preventDefault(); if (!pending) onSubmit(values); };
  return (
    <form id={id} className="ac-form" onSubmit={submit}>
      {fields.map((f, i) => f.type === 'select' ? (
        <label key={f.name} className="ac-field" data-kind="select">
          <select name={f.name} value={values[f.name] ?? ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}>
            {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span>{f.label}</span>
        </label>
      ) : (
        <label key={f.name} className="ac-field">
          <input name={f.name} type={f.type ?? 'text'} inputMode={f.type === 'number' ? 'decimal' : undefined} step={f.type === 'number' ? 'any' : undefined} required={f.required} placeholder=" " autoFocus={i === 0}
            value={values[f.name] ?? ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
          <span>{f.label}</span>
        </label>
      ))}
      <ErrorNote message={error} />
    </form>
  );
}

function CreateDialog({ t, locale, open, onClose, title, fields, m, onSubmit }: { t: Tr; locale: string; open: boolean; onClose: () => void; title: string; fields: FieldDef[]; m: Mutation; onSubmit: (v: Record<string, string>) => Promise<boolean> }) {
  const id = `ac-form-${useId().replace(/:/g, '')}`;
  return (
    <KitProvider locale={locale === 'ar' ? 'ar' : 'en'}>
      <Dialog open={open} onClose={onClose} title={title} size="sm" dismissible={!m.pending} className="ac-dialog"
        footer={<>
          <button type="button" className="ac-btn" data-variant="text" onClick={onClose} disabled={m.pending}>{t('crm.agency.cancel')}</button>
          <button type="submit" form={id} className="ac-btn" disabled={m.pending}>{m.pending ? t('crm.agency.saving') : t('crm.agency.add')}</button>
        </>}>
        <FormBody id={id} fields={fields} error={m.error} pending={m.pending} onReset={m.reset}
          onSubmit={(v) => { void onSubmit(v).then((ok) => { if (ok) onClose(); }); }} />
      </Dialog>
    </KitProvider>
  );
}

/** Only non-empty strings; numbers where the field is numeric. */
function clean(values: Record<string, string>, numeric: string[] = []): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    const s = v.trim();
    if (!s) continue;
    out[k] = numeric.includes(k) ? Number(s) : s;
  }
  return out;
}

interface CreateCtl { open: boolean; setOpen: (v: boolean) => void }
interface SectionProps { api: AgencyCrmApi; t: Tr; locale: string; initial: unknown; create: CreateCtl }

/* ---------- list sections: Google Contacts style rows ---------- */

interface Col { head: string; cell: (r: CrmRow) => ReactNode; /** shown at the trailing edge on every width (phones hide the other columns) */ trailing?: boolean }
interface ListDef {
  entity: 'leads' | 'contacts' | 'companies' | 'tasks' | 'activities';
  /** first column: avatar + this text */
  primary: (r: CrmRow) => unknown;
  primaryHead: string;
  /** second line under the name on phones */
  sub: (r: CrmRow) => ReactNode;
  cols: (m: Mutation) => Col[];
  fields: FieldDef[];
  payload: (v: Record<string, string>) => Record<string, unknown>;
  filters?: FilterDef[];
}

function ListSection({ api, t, locale, initial, create, def }: SectionProps & { def: ListDef }) {
  const list = useRows(() => api.list(def.entity), initial);
  const m = useMutation(t, list.reload);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const filters: FilterDef[] | undefined = def.filters && [{ id: 'all', label: t('crm.agency.filter.all'), test: () => true }, ...def.filters];
  const active = filters?.find((f) => f.id === filter) ?? filters?.[0];
  const shown = useMemo(() => (list.rows ?? []).filter((r) => matchesQuery(r, query) && (active ? active.test(r) : true)), [list.rows, query, active]);
  const cols = def.cols(m);
  const newLabel = t(NEW_KEY[def.entity]);
  return (
    <div className="ac-stack">
      <Toolbar t={t} query={query} onQuery={setQuery} count={t(countKey(def.entity, shown.length), { count: shown.length })}>
        {filters ? <Chips label={t('crm.agency.filter.label')} filters={filters} value={active?.id ?? 'all'} onChange={setFilter} /> : null}
      </Toolbar>
      {!create.open ? <ErrorNote message={m.error} /> : null}
      <Status rows={list.rows} error={list.error} t={t} />
      {list.rows && !list.error ? (shown.length ? (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <caption className="ac-sr">{t(`crm.agency.title.${def.entity}`)}</caption>
            <thead><tr><th scope="col">{def.primaryHead}</th>{cols.map((c, i) => <th key={i} scope="col" className={c.trailing ? 'ac-act' : 'ac-x'}>{c.head}</th>)}</tr></thead>
            <tbody>{shown.map((r) => (
              <tr key={r.id}>
                <td className="ac-maincell"><div className="ac-main"><Avatar v={def.primary(r)} />
                  <span className="ac-main__text"><strong><Text v={def.primary(r)} /></strong><span className="ac-sub">{def.sub(r)}</span></span></div></td>
                {cols.map((c, i) => <td key={i} data-label={c.head} className={c.trailing ? 'ac-act' : 'ac-x'}>{c.cell(r)}</td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <Empty t={t} filtered={(list.rows.length > 0)} onCreate={() => create.setOpen(true)} createLabel={newLabel} />) : null}
      <CreateDialog t={t} locale={locale} open={create.open} onClose={() => create.setOpen(false)} title={newLabel} fields={def.fields} m={m}
        onSubmit={(v) => m.run(() => api.create(def.entity, def.payload(v)))} />
    </div>
  );
}

const NEW_KEY = {
  pipeline: 'crm.agency.pipeline.newDeal', leads: 'crm.agency.leads.new', contacts: 'crm.agency.contacts.new', companies: 'crm.agency.companies.new',
  tasks: 'crm.agency.tasks.new', activities: 'crm.agency.activities.new',
} as const;

const dot = (...parts: ReactNode[]) => parts.filter((p) => p !== '' && p != null && p !== '-').map((p, i) => <span key={i}>{i ? ' · ' : ''}{p}</span>);

function Leads(p: SectionProps) {
  const { t } = p;
  const temp = (r: CrmRow) => (['hot', 'warm', 'cold'].includes(str(r.temperature)) ? t(`crm.leads.temperature.${str(r.temperature)}`) : '-');
  return <ListSection {...p} def={{
    entity: 'leads', primary: (r) => r.name ?? r.email, primaryHead: t('crm.agency.field.name'),
    sub: (r) => dot(str(r.companyName) ? <bdi>{str(r.companyName)}</bdi> : '', temp(r)),
    filters: (['hot', 'warm', 'cold'] as const).map((s) => ({ id: s, label: t(`crm.leads.temperature.${s}`), test: (r: CrmRow) => r.temperature === s })),
    cols: (m) => [
      { head: t('crm.agency.field.companyName'), cell: (r) => <Text v={r.companyName} /> },
      { head: t('crm.agency.field.channel'), cell: (r) => <Text v={r.channel} /> },
      { head: t('crm.agency.field.status'), cell: (r) => <Text v={r.status} /> },
      { head: t('crm.agency.field.score'), cell: (r) => str(r.score) || '-' },
      { head: t('crm.agency.field.temperature'), cell: temp },
      { head: '', trailing: true, cell: (r) => (r.status === 'new' ? <button type="button" className="ac-btn" data-variant="text" disabled={m.pending} onClick={() => void m.run(() => p.api.markContacted(r.id))}>{t('crm.agency.leads.contacted')}</button> : null) },
    ],
    fields: [{ name: 'name', label: t('crm.agency.field.name'), required: true }, { name: 'email', label: t('crm.agency.field.email'), type: 'email' }, { name: 'phone', label: t('crm.agency.field.phone') }, { name: 'companyName', label: t('crm.agency.field.companyName') }],
    payload: (v) => ({ ...clean(v), channel: 'manual' }),
  }} />;
}

function Contacts(p: SectionProps) {
  const { t } = p;
  return <ListSection {...p} def={{
    entity: 'contacts', primary: (r) => r.name, primaryHead: t('crm.agency.field.name'),
    sub: (r) => dot(str(r.email) ? <bdi>{str(r.email)}</bdi> : '', str(r.phone) ? <bdi>{str(r.phone)}</bdi> : ''),
    cols: () => [
      { head: t('crm.agency.field.email'), cell: (r) => <Text v={r.email} /> },
      { head: t('crm.agency.field.phone'), cell: (r) => <Text v={r.phone} /> },
      { head: t('crm.agency.field.title'), cell: (r) => <Text v={r.title} /> },
    ],
    fields: [{ name: 'name', label: t('crm.agency.field.name'), required: true }, { name: 'email', label: t('crm.agency.field.email'), type: 'email' }, { name: 'phone', label: t('crm.agency.field.phone') }, { name: 'title', label: t('crm.agency.field.title') }],
    payload: (v) => clean(v),
  }} />;
}

function Companies(p: SectionProps) {
  const { t } = p;
  return <ListSection {...p} def={{
    entity: 'companies', primary: (r) => r.name, primaryHead: t('crm.agency.field.name'),
    sub: (r) => dot(str(r.industry) ? <bdi>{str(r.industry)}</bdi> : '', str(r.website) ? <bdi>{str(r.website)}</bdi> : ''),
    cols: () => [
      { head: t('crm.agency.field.industry'), cell: (r) => <Text v={r.industry} /> },
      { head: t('crm.agency.field.website'), cell: (r) => <Text v={r.website} /> },
      { head: t('crm.agency.field.phone'), cell: (r) => <Text v={r.phone} /> },
    ],
    fields: [{ name: 'name', label: t('crm.agency.field.name'), required: true }, { name: 'industry', label: t('crm.agency.field.industry') }, { name: 'website', label: t('crm.agency.field.website') }, { name: 'phone', label: t('crm.agency.field.phone') }],
    payload: (v) => clean(v),
  }} />;
}

const TASK_STATUSES = ['todo', 'in_progress', 'review', 'done'] as const;
function Tasks(p: SectionProps) {
  const { t } = p;
  const priority = (r: CrmRow) => (['low', 'medium', 'high', 'urgent'].includes(str(r.priority)) ? t(`crm.agency.priority.${str(r.priority)}`) : '-');
  return <ListSection {...p} def={{
    entity: 'tasks', primary: (r) => r.title, primaryHead: t('crm.agency.field.title'),
    sub: (r) => dot(priority(r)),
    filters: TASK_STATUSES.map((s) => ({ id: s, label: t(`crm.agency.status.${s}`), test: (r: CrmRow) => r.status === s })),
    cols: (m) => [
      { head: t('crm.agency.field.priority'), cell: priority },
      { head: t('crm.agency.field.status'), trailing: true, cell: (r) => (
        <select className="ac-pick" aria-label={t('crm.agency.field.status')} value={str(r.status)} disabled={m.pending} onChange={(e) => void m.run(() => p.api.update('tasks', r.id, { status: e.target.value }))}>
          {TASK_STATUSES.map((s) => <option key={s} value={s}>{t(`crm.agency.status.${s}`)}</option>)}
        </select>) },
    ],
    fields: [{ name: 'title', label: t('crm.agency.field.title'), required: true }],
    payload: (v) => clean(v),
  }} />;
}

const ACTIVITY_TYPES = ['call', 'meeting', 'task', 'note', 'email', 'whatsapp'] as const;
function Activities(p: SectionProps) {
  const { t } = p;
  const type = (r: CrmRow) => (ACTIVITY_TYPES.includes(str(r.type) as never) ? t(`crm.agency.type.${str(r.type)}`) : '-');
  const state = (r: CrmRow) => (r.status === 'done' ? t('crm.agency.status.done') : t('crm.agency.status.open'));
  return <ListSection {...p} def={{
    entity: 'activities', primary: (r) => r.title, primaryHead: t('crm.agency.field.title'),
    sub: (r) => dot(type(r), state(r)),
    filters: (['open', 'done'] as const).map((s) => ({ id: s, label: t(`crm.agency.status.${s}`), test: (r: CrmRow) => (s === 'done' ? r.status === 'done' : r.status !== 'done') })),
    cols: () => [
      { head: t('crm.agency.field.type'), cell: type },
      { head: t('crm.agency.field.status'), cell: state },
    ],
    fields: [
      { name: 'title', label: t('crm.agency.field.title'), required: true },
      { name: 'type', label: t('crm.agency.field.type'), type: 'select', initial: 'call', options: ACTIVITY_TYPES.map((x) => ({ value: x, label: t(`crm.agency.type.${x}`) })) },
    ],
    payload: (v) => clean(v),
  }} />;
}

/* ---------- pipeline: board lanes on desktop, stage chips + one list on phones (same DOM) ---------- */

function Pipeline({ api, t, locale, initial, create }: SectionProps) {
  const deals = useRows(() => api.list('deals'), initial);
  const m = useMutation(t, deals.reload);
  const [query, setQuery] = useState('');
  const [picked, setStage] = useState<(typeof DEAL_STAGES)[number] | null>(null);
  const columns = dealColumns(deals.rows ?? [], query);
  // Phones show one stage at a time: the chosen one, else the first stage that has deals.
  const stage = picked ?? columns.find((c) => c.items.length)?.stage ?? DEAL_STAGES[0];
  const shownCount = columns.reduce((n, c) => n + c.items.length, 0);
  const currency = str(deals.rows?.[0]?.currency) || 'AED';
  const newLabel = t(NEW_KEY.pipeline);
  const ready = !!deals.rows && !deals.error;
  return (
    <div className="ac-stack">
      <Toolbar t={t} query={query} onQuery={setQuery} count={t(countKey('deals', shownCount), { count: shownCount })} />
      {!create.open ? <ErrorNote message={m.error} /> : null}
      <Status rows={deals.rows} error={deals.error} t={t} />
      {ready && !deals.rows!.length ? <Empty t={t} filtered={false} onCreate={() => create.setOpen(true)} createLabel={newLabel} /> : null}
      {ready && deals.rows!.length ? (
        <>
          <div className="ac-chips ac-stagebar" role="group" aria-label={t('crm.agency.pipeline.stages')}>
            {columns.map((c) => (
              <button key={c.stage} type="button" className="ac-chip" aria-pressed={stage === c.stage} onClick={() => setStage(c.stage)}>
                {t(`crm.deals.stage.${c.stage}`)} <span className="ac-chip__n">{c.items.length}</span>
              </button>
            ))}
          </div>
          <div className="ac-board" data-testid="crm-board">
            {columns.map((col) => (
              <section key={col.stage} className="ac-lane" aria-labelledby={`ac-col-${col.stage}`} data-stage={col.stage} data-active={stage === col.stage}>
                <header className="ac-lane__head">
                  <h2 id={`ac-col-${col.stage}`}>{t(`crm.deals.stage.${col.stage}`)}</h2>
                  <span className="ac-muted">{t(countKey('deals', col.items.length), { count: col.items.length })} · {money(locale, col.total, currency)}</span>
                </header>
                {col.items.length ? (
                  <ul className="ac-deals">
                    {col.items.map((d) => (
                      <li key={d.id} className="ac-deal">
                        <Avatar v={d.title} />
                        <div className="ac-deal__text"><strong><bdi>{str(d.title)}</bdi></strong><span className="ac-muted">{money(locale, d.valueMinor, d.currency)}</span></div>
                        <select className="ac-pick" aria-label={t('crm.agency.pipeline.moveTo')} value="" disabled={m.pending} onChange={(e) => void m.run(() => api.update('deals', d.id, { stage: e.target.value }))}>
                          <option value="" disabled hidden>{t('crm.agency.pipeline.moveTo')}</option>
                          {DEAL_STAGES.filter((s) => s !== d.stage).map((s) => <option key={s} value={s}>{t(`crm.deals.stage.${s}`)}</option>)}
                        </select>
                      </li>
                    ))}
                  </ul>
                ) : <p className="ac-muted ac-lane__empty">{query ? t('crm.agency.noMatch') : t('crm.agency.pipeline.noDeals')}</p>}
              </section>
            ))}
          </div>
        </>
      ) : null}
      <CreateDialog t={t} locale={locale} open={create.open} onClose={() => create.setOpen(false)} title={newLabel} m={m}
        fields={[{ name: 'title', label: t('crm.agency.pipeline.dealTitle'), required: true }, { name: 'value', label: t('crm.agency.field.value'), type: 'number' }]}
        onSubmit={(v) => m.run(() => api.create('deals', dealPayload(v, currency)))} />
    </div>
  );
}

function ImportSection({ api, t }: SectionProps) {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mapping = mappingFromCsv(csv);
  const run = async (fn: () => Promise<void>) => { setBusy(true); setMsg(null); try { await fn(); } catch (e) { setMsg(t(errorKey(e))); } finally { setBusy(false); } };
  const idem = useRef<string | null>(null);
  return (
    <div className="ac-stack ac-import">
      <p className="ac-muted">{t('crm.agency.import.intro')}</p>
      <label className="ac-field" data-kind="area">
        <textarea rows={8} dir="auto" placeholder=" " value={csv} onChange={(e) => { setCsv(e.target.value); setPreview(null); setDone(false); idem.current = null; }} />
        <span>{t('crm.agency.import.csv')}</span>
      </label>
      {csv && !Object.keys(mapping).length ? <p className="ac-note" data-tone="warn">{t('crm.agency.import.noMapping')}</p> : null}
      <div className="ac-actions">
        <button type="button" className="ac-btn" data-variant={preview && !done ? 'tonal' : undefined} disabled={busy || !Object.keys(mapping).length}
          onClick={() => void run(async () => { idem.current ??= `imp-${Date.now()}-${Math.random().toString(36).slice(2)}`; setPreview(await api.importPreview(csv, mapping, idem.current)); setDone(false); })}>{t('crm.agency.import.preview')}</button>
        {preview && !done ? <button type="button" className="ac-btn" disabled={busy} onClick={() => void run(async () => { await api.importCommit(preview.id); setDone(true); })}>{t('crm.agency.import.commit')}</button> : null}
      </div>
      {preview ? (
        <ul className="ac-kv" data-testid="crm-import-preview">
          <li>{t('crm.agency.import.creates', { count: preview.creates })}</li><li>{t('crm.agency.import.updates', { count: preview.updates })}</li>
          <li>{t('crm.agency.import.duplicates', { count: preview.duplicates })}</li><li>{t('crm.agency.import.rejected', { count: preview.rejected })}</li>
        </ul>
      ) : null}
      {done ? <p role="status" className="ac-note" data-tone="good">{t('crm.agency.import.committed')}</p> : null}
      {msg ? <p role="alert" className="ac-note" data-tone="bad">{msg}</p> : null}
    </div>
  );
}

const SECTION_VIEWS: Record<CrmSection, (p: SectionProps) => ReactNode> = {
  pipeline: Pipeline, leads: Leads, contacts: Contacts, companies: Companies, tasks: Tasks, activities: Activities, import: ImportSection,
};

/** Section tabs, shown above every CRM screen: one scrollable row, the active tab kept in view. */
export function AgencyCrmNav({ t, base }: { t: Tr; base: string }) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('[aria-current="page"]');
    el?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, []);
  return (
    <nav ref={ref} className="ac-subnav" aria-label={t('crm.agency.subnav')} data-testid="crm-subnav">
      {CRM_SECTIONS.map((s) => <NavLink key={s} to={sectionPath(base, s)} end>{t(`crm.agency.nav.${s}`)}</NavLink>)}
    </nav>
  );
}

function Workspace({ http, t, locale, section, base, preload, actionSlot }: AgencyCrmProps) {
  const api = useMemo(() => agencyCrmApi(http), [http]);
  const [open, setOpen] = useState(false);
  const View = SECTION_VIEWS[section];
  const newKey = section === 'import' ? null : NEW_KEY[section];
  const label = newKey ? t(newKey) : '';
  const primary = newKey ? (
    <button type="button" className="ac-btn ac-btn--head" aria-haspopup="dialog" onClick={() => setOpen(true)} data-testid="crm-new"><Plus /><span>{label}</span></button>
  ) : null;
  return (
    <div className="ac" data-testid={`crm-${section}`}>
      {actionSlot === undefined ? (primary ? <div className="ac-head-inline">{primary}</div> : null) : actionSlot && primary ? createPortal(primary, actionSlot) : null}
      <AgencyCrmNav t={t} base={base} />
      <View api={api} t={t} locale={locale} initial={preload?.[section]} create={{ open, setOpen }} />
      {newKey ? <button type="button" className="ac-fab" aria-haspopup="dialog" onClick={() => setOpen(true)} data-testid="crm-fab"><Plus /><span>{label}</span></button> : null}
    </div>
  );
}

export function AgencyCrm(props: AgencyCrmProps) {
  return <Workspace key={props.section} {...props} />;
}

export type { Availability, CrmEntityPath };
